import { GlyphText, type GlyphKind } from '@/shared/ui/components';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';

/**
 * StatusBadge — Atom
 * Displays appointment status as a glyph + plain label (no filled pill); colours are the glyph kinds' own.
 * Maps to FHIR Appointment.status (documentation only — Stream 1 uses custom DTOs).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CANONICAL — DO NOT "dedupe" into BridgeStatusChip.
 * Per Scripsteam/scrips-signal-ds `contracts/StatusBadge.json` (LOCKED v2.3),
 * THIS component is the canonical Scrips primitive for the closed-enum status
 * pill (the `statusAxis`: arrived / confirmed / booked / no-show / cancelled /
 * waitlisted / checked-in / fulfilled). It owns the per-status color binding.
 *
 * Signal `BridgeStatusChip` is a DIFFERENT primitive: a GENERIC semantic chip keyed by
 * `semantic` (success | error | warning | info | neutral | primary) + `tone`
 * (solid | soft). It has no concept of an appointment-status axis and no
 * per-status color map. BridgeStatusChip is therefore NOT a superset of StatusBadge —
 * collapsing the 8 appointment statuses onto 6 generic semantics would lose the
 * booked/checkedIn/arrived/fulfilled distinctions this component encodes.
 *
 * Decision D-010 (design-fix/DRIFT_CATALOG.md): KEEP. Not a duplicate. ~8 consumers.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Status is a small shape + colour + plain label, never a filled or tinted pill. The schedule
 * progression: Booked / Confirmed hollow -> Arrived / Checked in half-filled blue -> Completed green
 * check; No Show is the red alert. Cancelled stays hollow (its label still says Cancelled).
 * Waitlisted has no step on the progression, so it falls to the hollow glyph.
 */
export const STATUS_GLYPH = {
  arrived: 'half',
  confirmed: 'empty',
  booked: 'empty',
  noShow: 'alert',
  cancelled: 'empty',
  waitlisted: 'empty',
  checkedIn: 'half',
  fulfilled: 'done',
} as const satisfies Record<string, GlyphKind>;

export type AppointmentStatusType = keyof typeof STATUS_GLYPH;

/**
 * Canonical mapping: a server status CODE (Flutter `app_constants.dart:697-707`
 * — Arrived, Booked, CheckedIn, Fulfilled, Cancelled, NoShow, Proposed,
 * Pending, Waitlist, EnteredInError) OR a human display string ("No Show",
 * "Completed", "Checked In") → the closed `AppointmentStatusType` axis.
 * Normalises case + spaces/hyphens/underscores, so it accepts BOTH the wire
 * code and the API display.
 *
 * This is the SINGLE source every appointment-status surface maps through, so a
 * given status looks identical across the whole app (day list, calendar tiles,
 * by-doctor / by-room, patient appointments + chart). Render the result with
 * `<StatusBadge status={...} />`.
 *
 *   Fulfilled / Completed                 → fulfilled  (green)
 *   Cancelled / EnteredInError            → cancelled  (grey — both are void)
 *   Waitlist / Waitlisted / Proposed / Pending → waitlisted (tentative, yellow)
 *   Arrived / CheckedIn / Booked / NoShow → their own buckets
 *
 * There is NO backend "Confirmed" code — the `confirmed` bucket is a guard.
 * An unknown/new status falls back to the neutral `booked` treatment while the
 * caller still shows its raw label (so a wire regression stays visible in QA).
 */
export function appointmentStatusFromCode(
  raw: string | null | undefined,
): AppointmentStatusType {
  const c = (raw ?? '').toLowerCase().replace(/[\s_-]/g, '');
  if (c === 'arrived') return 'arrived';
  if (c === 'checkedin') return 'checkedIn';
  if (c === 'fulfilled' || c === 'completed') return 'fulfilled';
  // BUSINESS DECISION (not a bug): EnteredInError is a void/mistake record, so
  // it shares the muted `cancelled` bucket. Do NOT "fix" this to unknown.
  if (c === 'cancelled' || c === 'canceled' || c === 'enteredinerror')
    return 'cancelled';
  if (c === 'noshow') return 'noShow';
  // BUSINESS DECISION (not a bug): Pending + Proposed both mean "not yet
  // confirmed", so they share the tentative `waitlisted` (yellow) bucket with
  // Waitlist. Do NOT "fix" Pending back to its own/unknown treatment.
  if (c === 'waitlist' || c === 'waitlisted' || c === 'proposed' || c === 'pending')
    return 'waitlisted';
  if (c === 'confirmed') return 'confirmed';
  if (c === 'booked') return 'booked';
  return 'booked';
}

export const STATUS_LABELS: Record<AppointmentStatusType, string> = {
  arrived: 'Arrived',
  confirmed: 'Confirmed',
  booked: 'Booked',
  noShow: 'No Show',
  cancelled: 'Cancelled',
  waitlisted: 'Waitlisted',
  checkedIn: 'Checked In',
  // The `fulfilled` status code surfaces to users as "Completed" (matches the
  // calendar/list/patient views). The wire/status code stays `fulfilled`.
  fulfilled: 'Completed',
};

interface StatusBadgeProps {
  status?: AppointmentStatusType | null;
  className?: string;
  label?: string;
}

export function StatusBadge({ status = 'booked', label, className }: StatusBadgeProps) {
  // `fulfilled` always surfaces to users as "Completed" — override even an
  // explicit `label`, because the API `display` for this status is sometimes
  // "Fulfilled" (e.g. Room/Doctor calendars pass `label={statusDisplay}`).
  // The wire/status code stays `fulfilled`; this is display-only.
  // `||` (not `??`): an EMPTY-string label (an empty API `display`) must also
  // fall back to the canonical label, else the pill renders blank. Six surfaces
  // pass `label={display}`; some displays can be "" (CoS review PR #675 §4).
  const displayLabel = status === 'fulfilled' ? 'Completed' : (label || (status ? STATUS_LABELS[status] : undefined));
  // An unmapped / unrecognised status (callers do pass raw strings) renders the hollow glyph.
  const kind: GlyphKind = (status && STATUS_GLYPH[status]) || 'empty';
  return (
    <GlyphText kind={kind} className={className}>
      {displayLabel}
    </GlyphText>
  );
}

/**
 * An appointment status as the soft status chip, for tables (Antonin, 13 Oct: "Device Sessions status chips should
 * also be soft chips"). Same buckets as StatusBadge, read as a meaning: done is success, in the building or on the way
 * is info, tentative is warning, no-show is error, booked and void are neutral.
 */
export const BRIDGE_APPOINTMENT_STATUS_SEMANTIC: Record<AppointmentStatusType, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  arrived: 'info',
  checkedIn: 'info',
  confirmed: 'success',
  fulfilled: 'success',
  booked: 'neutral',
  waitlisted: 'warning',
  noShow: 'error',
  cancelled: 'neutral',
};

export function AppointmentStatusChip({ status = 'booked', label, className }: { status?: AppointmentStatusType; label?: string; className?: string }) {
  const displayLabel = status === 'fulfilled' ? 'Completed' : (label || STATUS_LABELS[status]);
  return (
    <BridgeStatusChip semantic={BRIDGE_APPOINTMENT_STATUS_SEMANTIC[status]} className={cn('whitespace-nowrap', className)}>
      {displayLabel}
    </BridgeStatusChip>
  );
}
